import { Indicator, Popover } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import ArrowLeft from '../../../assets/icons/ArrowLeft';
import BellIcon from '../../../assets/icons/BellIcon';
import menuIcon from '../../../assets/icons/menuIcon.png';
import { getNotifications } from '../../../services/usageRights.service';
import { Heading } from '../../styledComponents';
import styles from './HeaderComponent.module.css';
import Notification from './Notification/Notification';
import { NotificationType } from './types';

type HeaderComponentProps = {
  heading: string;
  setShowSidebar: React.Dispatch<React.SetStateAction<boolean>>;
  rightContent?: React.ReactNode;
  onBack?: () => void;
};

export default function HeaderComponent({ heading, setShowSidebar, rightContent, onBack }: HeaderComponentProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Single query for all notifications
  const { data: notifications = [], refetch } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await getNotifications();
      if (!response.status) {
        throw new Error(response.message || 'Failed to fetch notifications');
      }
      return response.data;
    },
    // Refresh every 30 seconds when the popover is open
    refetchInterval: isPopoverOpen ? 30000 : false,
    // Don't auto-refresh when window regains focus if popover is closed
    refetchOnWindowFocus: isPopoverOpen,
  });

  const unseen = notifications.some((notification: NotificationType) => !notification.seen);

  const handleNotificationClick = () => {
    setIsPopoverOpen(true);
    refetch();
  };

  return (
    <div className={styles.headerComponent}>
      <div className={`${styles.headerComponentLeft} ${onBack ? styles.headerWithBack : ''}`}>
        {onBack ? (
          <button type="button" className={styles.backButton} onClick={onBack} aria-label="Go back">
            <ArrowLeft width={24} height={24} color="var(--icon-default)" />
          </button>
        ) : (
          <button type="button" className={styles.sidebarToggleButton} onClick={() => setShowSidebar(true)}>
            <img src={menuIcon} alt="menu" width={32} />
          </button>
        )}
        {onBack && <Heading as="h2">{heading}</Heading>}
      </div>
      {!onBack && <Heading as="h2">{heading}</Heading>}
      <div className={styles.headerComponentRight}>
        <Popover position="bottom-end" withArrow shadow="md" opened={isPopoverOpen} onChange={setIsPopoverOpen}>
          <Popover.Target>
            <div className={styles.bellWrapper} onClick={handleNotificationClick} role="button" tabIndex={0}>
              <div className={styles.bellItem}>
                <Indicator color="red" offset={7} size={6} disabled={!unseen}>
                  <BellIcon />
                </Indicator>
              </div>
            </div>
          </Popover.Target>
          <Popover.Dropdown style={{ padding: 0, border: 0 }}>
            <Notification notifications={notifications} triggerFetch={refetch} />
          </Popover.Dropdown>
        </Popover>
        {rightContent}
      </div>
    </div>
  );
}
